import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { css } from '@styled-system/css';
import { useAtom } from 'jotai';
import React, { useCallback, useRef, useState } from 'react';

import {
  Alert,
  Box,
  Button,
  Drawer,
  EntryList,
  Flex,
  Grid,
  Header,
  Icon,
  List,
  Pagination,
  Radio,
  ScrollBox,
} from '~/components/blocks';
import { Sex } from '~/graphql/generated/graphql';
import { newPatientDrawerState } from '~/state/patient/atoms';
import { NewPatientPanelMap } from '~/state/patient/types';
import { Label } from '~/utils/label';

import { DirectVisitorCandidate } from '../DirectVisitorCandidate';
import { DEFAULT_PER_PAGE } from '../InputNewPatientPanel/use-submit-new-patient';
import { useFetchDirectVisitors } from '../use-fetch-direct_visitors';
import { useCreateOrOpenDirectVisitor } from './use-create-or-open-direct-visitor';

const SelectionList = styled(List)(({ theme }) =>
  css({ borderTop: 'none', marginTop: theme.space.m }),
);

const NewRadio = styled(Radio)(({ theme }) =>
  css({
    '& > p': {
      marginLeft: `calc(${theme.space.m} + ${theme.space.s})`,
    },
  }),
);

export const ConfirmNewPatientPanel = () => {
  const theme = useTheme();
  const scrollRef = useRef<RefAttributeType<typeof ScrollBox> | null>(null);

  const [{ inputValue, page, totalPage, existingPhoneNumber }, setState] =
    useAtom(newPatientDrawerState);
  const [selectedValue, setSelectedValue] = useState<string>('new');

  const { loading, directVisitors } = useFetchDirectVisitors();
  const { creating, error, boxRef, createOrVisit } = useCreateOrOpenDirectVisitor();

  const isSelectable = !loading && !creating;

  const handleChangePage = useCallback(
    (page: number) =>
      setState((_state) => ({
        ..._state,
        page: page,
        perPage: DEFAULT_PER_PAGE,
      })),
    [setState],
  );
  const handleClickBack = useCallback(
    () =>
      setState((_state) => ({
        ..._state,
        currentPanel: NewPatientPanelMap.input,
      })),
    [setState],
  );
  const handleClickCandidate = useCallback((visitorId: string) => setSelectedValue(visitorId), []);
  const handleClickNew = useCallback(() => setSelectedValue('new'), []);
  const handleSubmit = useCallback(async () => {
    await createOrVisit(selectedValue);
  }, [createOrVisit, selectedValue]);

  return (
    <Grid height="100%" gridTemplateRows="min-content 1fr min-content" overflow="auto">
      <Header attached="drawer">患者登録</Header>
      <Box overflow="auto" ref={boxRef} paddingX={theme.space.l}>
        {error && <Alert marginBottom={theme.space.l}>{error}</Alert>}
        {!existingPhoneNumber && (
          <Alert status="info" marginBottom={theme.space.l}>
            入力内容を確認のうえ、「確定」ボタンをクリックしてください。
          </Alert>
        )}
        <EntryList size="s">
          <EntryList.Head>氏名</EntryList.Head>
          <EntryList.Body>
            {inputValue
              ? `${inputValue.familyName} ${inputValue.givenName}（${inputValue.phoneticFamilyName} ${inputValue.phoneticGivenName}）`
              : 'ー'}
          </EntryList.Body>
        </EntryList>
        <EntryList size="s">
          <EntryList.Head>生年月日</EntryList.Head>
          <EntryList.Body>
            {inputValue?.birthDate ? Label.warekiBirthDate(inputValue.birthDate) : 'ー'}
          </EntryList.Body>
        </EntryList>
        <EntryList size="s">
          <EntryList.Head>性別</EntryList.Head>
          <EntryList.Body>
            {inputValue?.sex ? Label.sex(inputValue.sex as Sex) : 'ー'}
          </EntryList.Body>
        </EntryList>
        <EntryList size="s">
          <EntryList.Head>電話番号（携帯）</EntryList.Head>
          <EntryList.Body>{inputValue?.mobilePhone ?? 'ー'}</EntryList.Body>
        </EntryList>
        {existingPhoneNumber && (
          <>
            <Alert status="warning" marginTop={theme.space.l}>
              同じ電話番号で登録された患者がいます。フォローアップを行う患者または「新規患者として登録」を選択して確定してください。
            </Alert>
            <Box overflow="auto">
              <ScrollBox ref={scrollRef} loaderLogoSize="half" loading={loading}>
                <SelectionList selectable={isSelectable}>
                  {directVisitors.map((directVisitor, idx) => (
                    <List.Item key={idx}>
                      <DirectVisitorCandidate
                        disabled={!isSelectable}
                        candidate={directVisitor}
                        radioName="directVisitor"
                        radioValue={selectedValue}
                        onClick={handleClickCandidate}
                      />
                    </List.Item>
                  ))}
                  <List.Item onClick={handleClickNew}>
                    <Flex alignItems="center" paddingX={theme.space.m}>
                      <NewRadio
                        name="directVisitor"
                        label="新規患者として登録"
                        value="new"
                        checked={selectedValue === 'new'}
                        onChange={handleClickNew}
                      />
                    </Flex>
                  </List.Item>
                </SelectionList>
              </ScrollBox>
              {totalPage > 1 && (
                <Flex justifyContent="center" marginTop={theme.space.m}>
                  <Pagination
                    currentPage={page}
                    totalPage={totalPage}
                    onChange={handleChangePage}
                  />
                </Flex>
              )}
            </Box>
          </>
        )}
        <Button
          size="s"
          use="white"
          marginTop={theme.space.l}
          disabled={loading || creating}
          onClick={handleClickBack}
        >
          <Icon icon="back-line" size="s" />
          戻る
        </Button>
      </Box>
      <Drawer.Footer>
        <Button
          use="base"
          wide="fill"
          size="l"
          disabled={loading}
          loading={creating}
          onClick={handleSubmit}
        >
          確定
        </Button>
      </Drawer.Footer>
    </Grid>
  );
};
